/* ============================================================================
   PANELVERSE — shared theme JS (vanilla, no dependencies).
   (a) TH/EN language toggle    — switches Shopify language (/ ↔ /th)
   (b) generic countdowns       — [data-countdown-to], per-element persistence
   (c) header basket count      — /cart.js -> [data-cart-count]
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     (a) TH/EN language toggle — real Shopify languages
     - English lives at /, Thai at /th (Settings › Languages). The page language
       comes from <html lang>, which Liquid sets from request.locale.
     - body.is-th / body.is-en follow the page language (CSS still honours
       [data-lang] / .lang-* blocks for any section that uses them).
     - clicking any [data-lang-toggle] submits the hidden localization form in
       the header (#pv-lang-form) so Shopify redirects to the same page in the
       other language; if the form is missing (e.g. COD layout) it rewrites the
       URL prefix instead.
  ------------------------------------------------------------------------ */
  function pageLang() {
    var l = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    return l.indexOf('th') === 0 ? 'th' : 'en';
  }

  function applyLang(lang) {
    var body = document.body;
    body.classList.toggle('is-th', lang === 'th');
    body.classList.toggle('is-en', lang === 'en');
    document.documentElement.setAttribute('data-pv-lang', lang);
    var toggles = document.querySelectorAll('[data-lang-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', lang === 'th' ? 'true' : 'false');
    }
  }

  function setLang(lang) {
    if (lang !== 'th' && lang !== 'en') return;
    if (lang === pageLang()) return;
    var form = document.getElementById('pv-lang-form');
    if (form) {
      var input = form.querySelector('[data-lang-input]');
      if (input) input.value = lang;
      form.submit();
      return;
    }
    var path = window.location.pathname.replace(/^\/th(?=\/|$)/, '') || '/';
    window.location.href = (lang === 'th' ? '/th' + (path === '/' ? '' : path) : path) +
      window.location.search + window.location.hash;
  }

  function initLang() {
    applyLang(pageLang());
    document.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('[data-lang-toggle]') : null;
      if (!btn) return;
      ev.preventDefault();
      var explicit = btn.getAttribute('data-lang-toggle'); // "th" | "en" | "" (flip)
      var next = (explicit === 'th' || explicit === 'en')
        ? explicit
        : (pageLang() === 'th' ? 'en' : 'th');
      setLang(next);
    });
  }

  /* ------------------------------------------------------------------------
     (b) Generic countdown
     Any element with [data-countdown-to] ticks DD:HH:MM:SS every second.

     data-countdown-to accepts:
       - an absolute date/time parseable by Date.parse()
         e.g. data-countdown-to="2026-08-01T00:00:00+07:00"
       - a relative duration "9d 14h 42m" (any of d/h/m/s, in order) —
         the deadline is computed on first visit and persisted per element
         in localStorage key "pv-countdown-<element id>" (concept behavior).

     Display targets inside the element (all optional):
       [data-cd="dd"] [data-cd="hh"] [data-cd="mm"] [data-cd="ss"]
     If none exist, the element's text becomes "DD:HH:MM:SS" spans.
  ------------------------------------------------------------------------ */
  function parseDeadline(el, index) {
    var raw = (el.getAttribute('data-countdown-to') || '').trim();
    if (!raw) return NaN;

    var abs = Date.parse(raw);
    if (!isNaN(abs)) return abs;

    var m = raw.match(/^(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/i);
    if (!m) return NaN;
    var secs = (Number(m[1]) || 0) * 86400 +
               (Number(m[2]) || 0) * 3600 +
               (Number(m[3]) || 0) * 60 +
               (Number(m[4]) || 0);
    if (secs <= 0) return NaN;

    var key = 'pv-countdown-' + (el.id || 'anon-' + index);
    var deadline = NaN;
    try {
      var saved = localStorage.getItem(key);
      if (saved && !isNaN(Number(saved))) {
        deadline = Number(saved);
        if (deadline <= Date.now()) { // expired: restart the window
          deadline = Date.now() + secs * 1000;
          localStorage.setItem(key, String(deadline));
        }
      } else {
        deadline = Date.now() + secs * 1000;
        localStorage.setItem(key, String(deadline));
      }
    } catch (e) {
      deadline = Date.now() + secs * 1000;
    }
    return deadline;
  }

  function countdownSpans(el) {
    var spans = {
      dd: el.querySelector('[data-cd="dd"]'),
      hh: el.querySelector('[data-cd="hh"]'),
      mm: el.querySelector('[data-cd="mm"]'),
      ss: el.querySelector('[data-cd="ss"]')
    };
    if (spans.dd || spans.hh || spans.mm || spans.ss) return spans;

    // no targets: build a plain DD:HH:MM:SS readout
    el.textContent = '';
    var units = ['dd', 'hh', 'mm', 'ss'];
    for (var i = 0; i < units.length; i++) {
      var s = document.createElement('span');
      s.setAttribute('data-cd', units[i]);
      s.textContent = '00';
      el.appendChild(s);
      if (i < units.length - 1) el.appendChild(document.createTextNode(':'));
    }
    return countdownSpans(el);
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  function initCountdowns() {
    var els = document.querySelectorAll('[data-countdown-to]');
    if (!els.length) return;

    var items = [];
    for (var i = 0; i < els.length; i++) {
      var deadline = parseDeadline(els[i], i);
      if (isNaN(deadline)) continue;
      items.push({ el: els[i], deadline: deadline, spans: countdownSpans(els[i]) });
    }
    if (!items.length) return;

    function tick() {
      var now = Date.now();
      for (var j = 0; j < items.length; j++) {
        var it = items[j];
        var left = Math.max(0, it.deadline - now) / 1000;
        var vals = {
          dd: pad(Math.floor(left / 86400)),
          hh: pad(Math.floor(left / 3600) % 24),
          mm: pad(Math.floor(left / 60) % 60),
          ss: pad(Math.floor(left) % 60)
        };
        for (var k in vals) {
          if (it.spans[k] && it.spans[k].textContent !== vals[k]) {
            it.spans[k].textContent = vals[k];
          }
        }
        if (left <= 0) it.el.setAttribute('data-countdown-expired', '');
      }
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------------
     (c) Basket count — refresh every [data-cart-count] badge from /cart.js
  ------------------------------------------------------------------------ */
  function refreshCartCount() {
    var badges = document.querySelectorAll('[data-cart-count]');
    if (!badges.length) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || typeof cart.item_count === 'undefined') return;
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = cart.item_count;
        }
      })
      .catch(function () { /* offline / theme preview — keep Liquid-rendered count */ });
  }

  /* ------------------------------------------------------------------------
     init
  ------------------------------------------------------------------------ */
  function init() {
    initLang();
    initCountdowns();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // small public surface for section scripts
  window.Panelverse = window.Panelverse || {};
  window.Panelverse.setLang = setLang;
  window.Panelverse.refreshCartCount = refreshCartCount;
})();
